import { Box } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaModal, RanaTable, RanaWarningText } from "@nens/ranalib";

import { TableEmptyHistory } from "@/components/table-empty-history";
import { Profile } from "@/lib/profile";
import { useFileQuery } from "@/queries/query-file";
import { useFileHistoryQuery } from "@/queries/query-file-history";
import { cancelButtonText } from "@/translations";
import { disabledFileHistoryProps } from "@/utils/disabled-file-history-props";
import { hasFileVersionMissingLayers } from "@/utils/hasFileVersionMissingLayers";

import { getMaps } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { CompleteFile } from "../../../types/file-reference-with-file";

interface PublicationDetailFileVersionModalProps {
  fileReference: CompleteFile;
  onClose: () => void;
}

export function PublicationDetailFileVersionModal({
  fileReference,
  onClose,
}: PublicationDetailFileVersionModalProps) {
  const [selectedRef, setSelectedRef] = useState(fileReference.ref);
  const intl = useIntl();

  const publicationMaps = usePublicationStore(getMaps);
  const replaceFile = usePublicationStore((state) => state.replaceFile);

  const filesHistoryQuery = useFileHistoryQuery(fileReference.path);
  const fileHistory = filesHistoryQuery.data?.items ?? [];
  const tableDataIds = fileHistory.map((item) => item.ref) ?? [];

  // Fetch the file data for the selected ref to get its descriptor
  const selectedFileQuery = useFileQuery(
    selectedRef !== fileReference.ref ? fileReference.path : undefined,
    selectedRef
  );

  const hasSelectedVersionMissingLayers = useMemo(() => {
    // Only validate if we selected a different ref
    if (selectedRef === fileReference.ref) return false;
    if (!selectedFileQuery.data || selectedFileQuery.isLoading) return false;

    const selectedFileDescriptor = selectedFileQuery.data.descriptor;
    return hasFileVersionMissingLayers(selectedFileDescriptor, fileReference, publicationMaps);
  }, [selectedRef, fileReference, selectedFileQuery, publicationMaps]);

  const editPublicationFiles = () => {
    // Update the file in the Zustand store
    const updatedFile = { ...fileReference, ref: selectedRef };
    replaceFile(updatedFile);

    onClose();
  };

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={
        <FormattedMessage id="heading.select_file_version" defaultMessage="Select a file version" />
      }
      maxW="fit"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          onClick: onClose,
        },
        {
          isDisabled:
            fileReference.ref === selectedRef ||
            hasSelectedVersionMissingLayers ||
            selectedFileQuery.isLoading,
          variant: "primary",
          onClick: () => {
            editPublicationFiles();
          },
          children: intl.formatMessage({
            id: "process.select",
            defaultMessage: "Select",
          }),
        },
      ]}
      bottomChild={
        <Box maxW="md">
          <RanaWarningText
            message={intl.formatMessage({
              id: "publication.file_version.modal.file_incompatible",
              defaultMessage:
                "File version is not compatible with the map layers in the current publication",
            })}
            show={hasSelectedVersionMissingLayers}
          />
        </Box>
      }
    >
      <RanaTable
        emptyElement={<TableEmptyHistory />}
        columnDefinitions={[
          {
            width: "table_cell_sm",
            title: intl.formatMessage({ id: "column.created_at", defaultMessage: "Created at" }),
          },
          {
            width: "table_cell_xs",
            title: intl.formatMessage({ id: "column.commited_by", defaultMessage: "Commited by" }),
          },
          {
            title: intl.formatMessage({
              id: "publication.detail.file.column.version",
              defaultMessage: "Version",
            }),
            width: "table_cell_xs",
          },
          {
            width: "table_cell_sm",
            title: "",
          },
        ]}
        tableDataIds={tableDataIds}
        isSkeleton={filesHistoryQuery.isLoading}
      >
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {fileHistory.map(({ ref, message, createdAt, committedBy }) => (
              <RanaTable.Row
                onClick={() => {
                  setSelectedRef(ref);
                }}
                isSelected={ref === selectedRef}
                key={ref}
                rowId={ref}
                {...disabledFileHistoryProps(intl, message)}
              >
                <RanaTable.Cell>{new Date(createdAt).toLocaleString()}</RanaTable.Cell>
                <RanaTable.Cell>
                  <Profile inTable user={committedBy} />
                </RanaTable.Cell>
                <RanaTable.Cell title={ref}>{ref?.slice(0, 7)}</RanaTable.Cell>
                <RanaTable.Cell>
                  {ref === fileReference.ref ? (
                    <FormattedMessage
                      id="file_ref.currently_active"
                      defaultMessage="Active in the publication"
                    />
                  ) : null}
                </RanaTable.Cell>
              </RanaTable.Row>
            ))}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
    </RanaModal>
  );
}
